import { api, ApiError } from './api'
import type {
  BillingReconciliation,
  MonthlyCollectionsPoint,
  MpesaTransaction,
  Payment,
  PaymentMethod,
  PaymentMethodBreakdownRow,
  PaymentsSummaryRow,
  RecordPaymentResult,
  UnmatchedC2BTransaction,
} from '@riverpark/types'

const API_BASE = import.meta.env.VITE_API_URL?.replace(/\/$/, '') ?? '/api'

export interface RecordPaymentInput {
  memberId: string
  amount: number
  method: 'cash' | 'bank_transfer' | 'manual_mpesa'
  reference?: string
  paidAt?: string
  notes?: string
  // Defaults to the FIFO-oldest-across-everything cascade when both are omitted. At most one
  // should be set.
  targetInvoiceId?: string
  targetUtilityTypeId?: string
}

export async function recordPayment(
  input: RecordPaymentInput,
): Promise<{ data: RecordPaymentResult }> {
  return api.post<{ data: RecordPaymentResult }>('/payments/manual', input)
}

export interface InitiateStkPushResult {
  transactionId: string
  checkoutRequestId: string
  customerMessage: string
  amount: number
}

export interface StkPushTarget {
  invoiceId?: string
  utilityTypeId?: string
}

/** Pays the member's full cumulative outstanding balance by default; pass `target.invoiceId` or
 * `target.utilityTypeId` (not both) to instead pay just that invoice or utility type — the API
 * computes the correct amount and Paybill account reference for whichever is targeted.
 * `phone` is optional — omit to charge the member's registered number. */
export async function initiateStkPush(
  phone?: string,
  target?: StkPushTarget,
): Promise<{ data: InitiateStkPushResult }> {
  return api.post<{ data: InitiateStkPushResult }>('/payments/mpesa/stk-push', {
    ...(phone && { phone }),
    ...(target?.invoiceId && { targetInvoiceId: target.invoiceId }),
    ...(target?.utilityTypeId && { targetUtilityTypeId: target.utilityTypeId }),
  })
}

export async function getMpesaTransaction(id: string): Promise<{ data: MpesaTransaction }> {
  return api.get<{ data: MpesaTransaction }>(`/payments/mpesa/transactions/${id}`)
}

export interface PayNowInfo {
  phone: string
  accountReference: string
  paybill: string
}

/** Phone (to prepopulate the editable STK field) + Paybill details, for the logged-in member's
 * own pay-now modal. */
export async function getPayNowInfo(utilityTypeId?: string): Promise<{ data: PayNowInfo }> {
  return api.get<{ data: PayNowInfo }>(
    `/payments/pay-info${utilityTypeId ? `?utilityTypeId=${encodeURIComponent(utilityTypeId)}` : ''}`,
  )
}

export interface QuickPayPeek extends PayNowInfo {
  memberName: string
  amount: number
}

/** Read-only — verifies the token and returns the amount due, no side effects. Used by the
 * no-login email quick-pay page before the member clicks "Pay now". */
export async function peekQuickPay(token: string): Promise<{ data: QuickPayPeek }> {
  return api.get<{ data: QuickPayPeek }>(`/payments/quick-pay/${token}`)
}

/** The actual pay action for the no-login email link — only ever call this from an explicit
 * button click, never automatically (email/link scanners pre-fetch GETs, never POSTs). `phone`
 * is optional — omit to charge the member's registered number. */
export async function payQuickPay(
  token: string,
  phone?: string,
): Promise<{ data: InitiateStkPushResult }> {
  return api.post<{ data: InitiateStkPushResult }>(`/payments/quick-pay/${token}`, {
    ...(phone && { phone }),
  })
}

export interface PaymentsReportRange {
  from: string
  to: string
  utilityTypeId?: string
  method?: PaymentMethod
}

function reportQuery({ from, to, utilityTypeId, method }: PaymentsReportRange): string {
  const params = new URLSearchParams({ from, to })
  if (utilityTypeId) params.set('utilityTypeId', utilityTypeId)
  if (method) params.set('method', method)
  return params.toString()
}

export async function getPaymentsSummary(
  range: PaymentsReportRange,
): Promise<{ data: PaymentsSummaryRow[] }> {
  return api.get<{ data: PaymentsSummaryRow[] }>(`/payments/summary?${reportQuery(range)}`)
}

export async function getBillingReconciliation(
  range: PaymentsReportRange,
): Promise<{ data: BillingReconciliation }> {
  return api.get<{ data: BillingReconciliation }>(`/payments/reconciliation?${reportQuery(range)}`)
}

export async function exportPayments(range: PaymentsReportRange): Promise<{ data: Payment[] }> {
  return api.get<{ data: Payment[] }>(`/payments/export?${reportQuery(range)}`)
}

export async function getCollectionsTrend(
  range: PaymentsReportRange,
): Promise<{ data: MonthlyCollectionsPoint[] }> {
  return api.get<{ data: MonthlyCollectionsPoint[] }>(`/payments/trend?${reportQuery(range)}`)
}

export async function getPaymentMethodBreakdown(
  range: PaymentsReportRange,
): Promise<{ data: PaymentMethodBreakdownRow[] }> {
  return api.get<{ data: PaymentMethodBreakdownRow[] }>(
    `/payments/method-breakdown?${reportQuery(range)}`,
  )
}

/** Fetches the estate report PDF from the API and triggers a client-side save — mirrors
 * downloadDocument() in lib/documents.ts, since the API lives on a separate subdomain and a
 * plain <a href> would bypass the auth cookie. */
export async function downloadEstateReport(from: string, to: string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/payments/estate-report?${new URLSearchParams({ from, to }).toString()}`,
    { credentials: 'include' },
  )
  if (!res.ok) throw new ApiError(res.status, 'Failed to generate report')
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `riverpark-estate-report-${from.slice(0, 10)}-to-${to.slice(0, 10)}.pdf`
  anchor.click()
  URL.revokeObjectURL(url)
}

/** Confirmed direct-Paybill payments staff couldn't auto-match to a member — the triage queue. */
export async function listUnmatchedC2BTransactions(): Promise<{
  data: UnmatchedC2BTransaction[]
}> {
  return api.get<{ data: UnmatchedC2BTransaction[] }>('/payments/c2b/unmatched')
}

export async function resolveC2BTransaction(
  transactionId: string,
  memberId: string,
  target?: StkPushTarget,
): Promise<{ data: RecordPaymentResult }> {
  return api.post<{ data: RecordPaymentResult }>(`/payments/c2b/${transactionId}/resolve`, {
    memberId,
    ...(target?.invoiceId && { targetInvoiceId: target.invoiceId }),
    ...(target?.utilityTypeId && { targetUtilityTypeId: target.utilityTypeId }),
  })
}

export interface ListPaymentsFilters {
  memberId?: string
  invoiceId?: string
  /** Forces "my own payments" regardless of role — needed because committee/estate_manager
   * accounts can also own a plot and call this from their own self-service page. */
  mine?: boolean
}

export async function listPayments(
  filters: ListPaymentsFilters = {},
): Promise<{ data: Payment[] }> {
  const params = new URLSearchParams()
  if (filters.memberId) params.set('memberId', filters.memberId)
  if (filters.invoiceId) params.set('invoiceId', filters.invoiceId)
  if (filters.mine) params.set('mine', 'true')
  const query = params.toString()
  return api.get<{ data: Payment[] }>(`/payments${query ? `?${query}` : ''}`)
}
